import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Trash2, Pencil, LogOut, X, Upload, Check, Star, Home, ImageDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const emptyCar = {
  marca: "", modello: "", descrizione: "",
  anno: "", km: "", cv: "", cilindrata: "",
  alimentazione: "", cambio: "", cilindri: "", trazione: "",
  prezzo: "", colore: "", foto: [], venduto: false,
};

function toNumber(v) {
  if (v === "" || v == null) return null;
  const n = Number(v);
  return isNaN(n) ? null : n;
}

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

export default function AdminPage() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [cars, setCars] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} | car
  const [filter, setFilter] = useState("disponibili"); // "totale" | "disponibili" | "vendute"
  const [optimizing, setOptimizing] = useState(false);

  const onOptimizeImages = async () => {
    if (!confirm("Comprimere tutte le foto esistenti e generare le miniature? L'operazione può richiedere alcuni minuti su cataloghi grandi.")) return;
    setOptimizing(true);
    try {
      const { data } = await api.post("/cars/_migrate/optimize-images");
      toast.success(`Ottimizzate ${data.processed} vetture (${data.skipped} già ok)`);
      await load();
    } catch {
      toast.error("Errore durante l'ottimizzazione");
    } finally {
      setOptimizing(false);
    }
  };

  useEffect(() => {
    if (user === false) nav("/login", { replace: true });
  }, [user, nav]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/cars", { params: { page: 1, limit: 200 } });
      setCars(data || []);
    } finally { setLoading(false); }
  };

  const loadReviews = async () => {
    try {
      const { data } = await api.get("/reviews");
      setReviews(data || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { if (user && user !== false) { load(); loadReviews(); } }, [user]);

  const onDeleteReview = async (id) => {
    if (!confirm("Eliminare questa recensione?")) return;
    try {
      await api.delete(`/reviews/${id}`);
      toast.success("Recensione eliminata");
      setReviews((rs) => rs.filter((r) => r.id !== id));
    } catch {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const stats = useMemo(() => ({
    total: cars.length,
    available: cars.filter((c) => !c.venduto).length,
    sold: cars.filter((c) => c.venduto).length,
  }), [cars]);

  const onDelete = async (id) => {
    if (!confirm("Eliminare definitivamente questa vettura?")) return;
    try {
      await api.delete(`/cars/${id}`);
      toast.success("Vettura eliminata");
      setCars((cs) => cs.filter((c) => c.id !== id));
    } catch (e) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const onSave = async (payload) => {
    const cleaned = {
      marca: payload.marca,
      modello: payload.modello,
      descrizione: payload.descrizione || "",
      anno: toNumber(payload.anno),
      km: toNumber(payload.km),
      cv: toNumber(payload.cv),
      cilindrata: toNumber(payload.cilindrata),
      alimentazione: payload.alimentazione || null,
      cambio: payload.cambio || null,
      cilindri: toNumber(payload.cilindri),
      trazione: payload.trazione || null,
      prezzo: toNumber(payload.prezzo),
      colore: payload.colore || null,
      foto: payload.foto || [],
      venduto: !!payload.venduto,
    };
    try {
      if (payload.id) {
        await api.put(`/cars/${payload.id}`, cleaned);
        toast.success("Vettura aggiornata");
      } else {
        await api.post("/cars", cleaned);
        toast.success("Vettura creata");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error("Errore durante il salvataggio");
    }
  };

  if (user === null) return null;

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-page">
      <div className="border-b border-white/10 sticky top-0 bg-black/80 backdrop-blur-xl z-40">
        <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-5 flex items-center justify-between">
          <div>
            <div className="font-serif text-2xl text-white">Pannello Admin</div>
            <div className="label-eyebrow text-neutral-500 mt-1">DP Supercar &amp; Classic Cars</div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setEditing({ ...emptyCar })} className="btn-magnetic btn-solid" data-testid="admin-new-car">
              <Plus size={16} /> Nuova vettura
            </button>
            <button onClick={onOptimizeImages} disabled={optimizing} className="btn-magnetic disabled:opacity-50" data-testid="admin-optimize" title="Comprimi e genera miniature per tutte le foto">
              <ImageDown size={14} /> {optimizing ? "Ottimizzo…" : "Ottimizza immagini"}
            </button>
            <button onClick={() => nav("/")} className="btn-magnetic" data-testid="admin-home">
              <Home size={14} /> Torna al sito
            </button>
            <button onClick={logout} className="btn-magnetic" data-testid="admin-logout">
              <LogOut size={14} /> Esci
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-[1600px] mx-auto px-6 sm:px-10 py-14">
        <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-14">
          {[
            { key: "totale", label: "Totale", value: stats.total },
            { key: "disponibili", label: "Disponibili", value: stats.available },
            { key: "vendute", label: "Vendute", value: stats.sold },
          ].map((s) => {
            const active = filter === s.key;
            return (
              <button
                type="button"
                key={s.key}
                onClick={() => setFilter(s.key)}
                className={`text-left p-8 transition-colors ${active ? "bg-white text-black" : "bg-[#0a0a0a] text-white hover:bg-white/[0.04]"}`}
                data-testid={`admin-filter-${s.key}`}
              >
                <div className={`label-eyebrow mb-3 ${active ? "text-black/60" : "text-neutral-500"}`}>{s.label}</div>
                <div className={`text-5xl font-light tabular-nums tracking-tight ${active ? "text-black" : "text-white"}`}>{s.value}</div>
              </button>
            );
          })}
        </div>

        {(() => {
          const filtered = filter === "totale" ? cars : filter === "vendute" ? cars.filter((c) => c.venduto) : cars.filter((c) => !c.venduto);
          if (loading) return <div className="h-64 bg-neutral-900/40 animate-pulse" />;
          if (filtered.length === 0) return (
          <div className="border border-white/10 py-24 text-center">
            <div className="label-eyebrow text-neutral-500 mb-4">
              {filter === "vendute" ? "Nessuna vettura venduta" : filter === "disponibili" ? "Nessuna vettura disponibile" : "Nessuna vettura"}
            </div>
            <div className="font-serif italic text-2xl text-neutral-400 mb-8">
              {filter === "vendute" ? "Le vetture segnate come vendute appariranno qui." : "Aggiungi la prima vettura al tuo catalogo."}
            </div>
            {filter !== "vendute" && (
              <button onClick={() => setEditing({ ...emptyCar })} className="btn-magnetic btn-solid" data-testid="admin-first-car">
                <Plus size={16} /> Aggiungi vettura
              </button>
            )}
          </div>
          );
          return (
          <div className="border border-white/10 divide-y divide-white/10">
            {filtered.map((c) => (
              <div key={c.id} className="flex items-center gap-6 p-4 hover:bg-white/[0.02] transition-colors" data-testid={`admin-car-row-${c.id}`}>
                <div className="w-24 h-16 bg-neutral-900 overflow-hidden flex-shrink-0">
                  {c.foto?.[0] && <img src={c.foto[0]} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-lg font-medium text-white truncate tracking-tight">
                    {c.marca} <span className="text-neutral-400 font-normal">{c.modello}</span>
                  </div>
                  <div className="text-xs text-neutral-500 mt-1">
                    {c.anno || "—"} · {c.km != null ? `${Number(c.km).toLocaleString("it-IT")} km` : "—"} · {c.cv ? `${c.cv} CV` : "—"} · {c.venduto ? "Venduta" : (c.prezzo === 0 ? "Trattativa Riservata" : (c.prezzo != null ? `€ ${Number(c.prezzo).toLocaleString("it-IT")}` : "P.s.r."))}
                  </div>
                </div>
                {c.venduto && <span className="label-eyebrow text-red-300/80">Venduta</span>}
                <button onClick={() => setEditing(c)} className="p-3 border border-white/10 hover:border-white/40 transition-colors" data-testid={`admin-edit-${c.id}`}>
                  <Pencil size={14} className="text-white" />
                </button>
                <button onClick={() => onDelete(c.id)} className="p-3 border border-white/10 hover:border-red-500/60 hover:bg-red-500/10 transition-colors" data-testid={`admin-delete-${c.id}`}>
                  <Trash2 size={14} className="text-white" />
                </button>
              </div>
            ))}
          </div>
          );
        })()}

        {/* Recensioni */}
        <div className="mt-20">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-serif text-3xl text-white">Recensioni</h2>
            <span className="label-eyebrow text-neutral-500">{reviews.length} totali</span>
          </div>
          {reviews.length === 0 ? (
            <div className="border border-white/10 py-16 text-center">
              <div className="font-serif italic text-xl text-neutral-400">Nessuna recensione ricevuta.</div>
            </div>
          ) : (
            <div className="border border-white/10 divide-y divide-white/10">
              {reviews.map((r) => (
                <div key={r.id} className="flex items-start gap-6 p-5 hover:bg-white/[0.02] transition-colors" data-testid={`admin-review-row-${r.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-serif text-lg text-white">{r.nome}</span>
                      <span className="flex items-center gap-0.5">
                        {[1, 2, 3, 4, 5].map((i) => (
                          <Star key={i} size={13} strokeWidth={1.5} className={i <= r.rating ? "fill-white text-white" : "text-neutral-700"} />
                        ))}
                      </span>
                    </div>
                    <p className="text-sm text-neutral-400">{r.commento}</p>
                  </div>
                  <button onClick={() => onDeleteReview(r.id)} className="p-3 border border-white/10 hover:border-red-500/60 hover:bg-red-500/10 transition-colors flex-shrink-0" data-testid={`admin-delete-review-${r.id}`}>
                    <Trash2 size={14} className="text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {editing && (
          <CarEditor
            initial={editing}
            onClose={() => setEditing(null)}
            onSave={onSave}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function CarEditor({ initial, onClose, onSave }) {
  const [f, setF] = useState({ ...emptyCar, ...initial });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    try {
      const urls = await Promise.all(files.map(fileToDataURL));
      setF((p) => ({ ...p, foto: [...(p.foto || []), ...urls] }));
    } catch { toast.error("Errore nel caricamento immagini"); }
    e.target.value = "";
  };

  const removePhoto = (i) => setF((p) => ({ ...p, foto: p.foto.filter((_, idx) => idx !== i) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.marca || !f.modello) { toast.error("Marca e modello obbligatori"); return; }
    setSaving(true);
    try { await onSave(f); } finally { setSaving(false); }
  };

  const fields = [
    ["anno", "Anno", "number"],
    ["km", "Chilometri", "number"],
    ["cv", "Potenza (CV)", "number"],
    ["cilindrata", "Cilindrata (cc)", "number"],
    ["cilindri", "Cilindri", "number"],
    ["alimentazione", "Alimentazione", "text"],
    ["cambio", "Cambio", "text"],
    ["trazione", "Trazione", "text"],
    ["colore", "Colore", "text"],
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-md flex items-start justify-center p-4 sm:pt-24 overflow-y-auto"
      data-testid="car-editor-modal"
    >
      <motion.form
        onSubmit={submit}
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-4xl bg-[#0a0a0a] border border-white/10 my-8"
      >
        <div className="flex items-start justify-between gap-4 p-6 border-b border-white/10 bg-[#0a0a0a]">
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-neutral-500 mb-3">{f.id ? "Modifica vettura" : "Nuova vettura"}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                value={f.marca ?? ""}
                onChange={(e) => set("marca", e.target.value)}
                placeholder="Marca *"
                className="w-full bg-transparent border border-white/10 px-4 py-3 text-xl font-medium text-white placeholder:text-neutral-600 focus:outline-none focus:border-white/40 tracking-tight"
                data-testid="editor-marca"
              />
              <input
                type="text"
                value={f.modello ?? ""}
                onChange={(e) => set("modello", e.target.value)}
                placeholder="Modello *"
                className="w-full bg-transparent border border-white/10 px-4 py-3 text-xl text-white placeholder:text-neutral-600 focus:outline-none focus:border-white/40 tracking-tight"
                data-testid="editor-modello"
              />
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-white/10 transition-colors flex-shrink-0" data-testid="editor-close">
            <X size={20} className="text-white" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div>
            <label className="label-eyebrow text-neutral-500 block mb-2">Descrizione</label>
            <textarea
              value={f.descrizione} onChange={(e) => set("descrizione", e.target.value)} rows={5}
              className="w-full bg-transparent border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-white/40"
              data-testid="editor-descrizione"
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {fields.map(([k, label, type]) => (
              <Field key={k} label={label} val={f[k]} onChange={(v) => set(k, v)} type={type} testId={`editor-${k}`} />
            ))}
          </div>

          {/* Prezzo — con opzione Trattativa Riservata */}
          <div className="border border-white/10 p-5">
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-end">
              <div>
                <label className="label-eyebrow text-neutral-500 block mb-2">Prezzo (€)</label>
                <input
                  type="number"
                  value={f.prezzo === 0 ? "" : (f.prezzo ?? "")}
                  onChange={(e) => set("prezzo", e.target.value === "" ? "" : Number(e.target.value))}
                  disabled={f.prezzo === 0}
                  placeholder={f.prezzo === 0 ? "Trattativa Riservata" : "es. 89000"}
                  className="w-full bg-transparent border border-white/10 px-4 py-3 text-white placeholder:text-neutral-600 focus:outline-none focus:border-white/40 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="editor-prezzo"
                />
              </div>
              <label className="inline-flex items-center gap-3 cursor-pointer select-none border border-white/10 hover:border-white/30 px-4 py-3 transition-colors" data-testid="editor-trattativa-toggle">
                <input
                  type="checkbox"
                  checked={f.prezzo === 0}
                  onChange={(e) => set("prezzo", e.target.checked ? 0 : "")}
                  className="w-5 h-5 accent-white"
                />
                <span className="text-white text-sm leading-tight">
                  Prezzo su richiesta<br/>
                  <span className="text-neutral-400 text-xs">Trattativa Riservata</span>
                </span>
              </label>
            </div>
            <p className="mt-3 text-xs text-neutral-500">
              Spuntando la casella, sul sito verrà mostrata la scritta <span className="text-white">Trattativa Riservata</span> al posto della cifra.
            </p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="label-eyebrow text-neutral-500">Foto</label>
              <label className="cursor-pointer inline-flex items-center gap-2 label-eyebrow text-white border border-white/20 px-3 py-2 hover:bg-white/10 transition-colors" data-testid="editor-upload-photos">
                <Upload size={14} />
                Aggiungi foto
                <input type="file" multiple accept="image/*" onChange={onFiles} className="hidden" />
              </label>
            </div>
            {f.foto?.length ? (
              <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                {f.foto.map((src, i) => (
                  <div key={i} className="relative group aspect-[4/3] overflow-hidden border border-white/10">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/70 text-white p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-photo-${i}`}>
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-dashed border-white/10 py-10 text-center text-neutral-500 text-sm">Nessuna foto caricata</div>
            )}
          </div>

          <label className="inline-flex items-center gap-3 cursor-pointer" data-testid="editor-venduto-toggle">
            <input type="checkbox" checked={!!f.venduto} onChange={(e) => set("venduto", e.target.checked)} className="w-5 h-5 accent-white" />
            <span className="text-white">Segna come venduta</span>
          </label>
        </div>

        <div className="p-6 border-t border-white/10 flex justify-end gap-3 sticky bottom-0 bg-[#0a0a0a]">
          <button type="button" onClick={onClose} className="btn-magnetic" data-testid="editor-cancel">Annulla</button>
          <button type="submit" disabled={saving} className="btn-magnetic btn-solid disabled:opacity-50" data-testid="editor-save">
            <Check size={16} /> {saving ? "Salvo…" : "Salva"}
          </button>
        </div>
      </motion.form>
    </motion.div>
  );
}

function Field({ label, val, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="label-eyebrow text-neutral-500 block mb-2">{label}</label>
      <input
        type={type}
        value={val ?? ""} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-white/40"
        data-testid={testId}
      />
    </div>
  );
}
